// Finance — what the firm has out on cases, and what has come back.
//
// Aaron's ask: see at a glance that the firm has $125k invested in live
// cases, and open that up the way the attorney workload card opens up.
//
// Two sections rather than two pages. "How much are we out" and "what
// came back" are read together — a partner comparing them should not
// have to navigate between them.
//
// The revenue half is honest about being half. Gross settlement figures
// are real; what the FIRM made depends on fee terms nobody has entered,
// and the page says so where the number would have been rather than
// rendering a blank a reader would take for zero.

import React, { useCallback, useEffect, useState } from "react";
import { C } from "../constants.js";
import { apiFetch } from "../api.js";
import { Spinner } from "../components/Spinner.jsx";

const RANGES = [
  { days: 90, label: "90 days" },
  { days: 365, label: "12 months" },
  { days: 1825, label: "All time" },
];

function money(n) {
  if (n == null) return "—";
  return n.toLocaleString(undefined, {
    style: "currency", currency: "USD", maximumFractionDigits: 0,
  });
}

export function FinancePage({ section = "costs", onMatterClick }) {
  const [tab, setTab] = useState(section);
  const [costs, setCosts] = useState(null);
  const [rev, setRev] = useState(null);
  const [days, setDays] = useState(365);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      if (tab === "costs") setCosts(await apiFetch("/api/finance/costs"));
      else setRev(await apiFetch(`/api/finance/revenue?days=${days}`));
    } catch (e) {
      setError(e.message);
    }
  }, [tab, days]);

  useEffect(() => { load(); }, [load]);

  const data = tab === "costs" ? costs : rev;

  return React.createElement("div", { style: { padding: "32px 40px", maxWidth: "1000px" } },
    React.createElement("h1", {
      style: { fontFamily: "'Playfair Display', serif", fontSize: "32px",
               color: C.navy, marginBottom: "6px" },
    }, "Finance"),
    React.createElement("p", {
      style: { fontSize: "14px", color: C.textMuted, marginBottom: "18px" },
    }, "What the firm has out on cases, and what has come back."),

    React.createElement("div", {
      className: "wrap-row",
      style: { display: "flex", gap: "8px", marginBottom: "18px" },
    },
      ...[["costs", "Case costs out"], ["revenue", "Revenue"]].map(([k, l]) =>
        React.createElement("button", {
          key: k, onClick: () => setTab(k),
          style: {
            border: `1px solid ${tab === k ? C.navy : C.warmGray}`,
            background: tab === k ? C.navy : "#fff",
            color: tab === k ? "#fff" : C.navy,
            borderRadius: "7px", padding: "7px 15px", fontSize: "12.5px",
            fontWeight: 600, cursor: "pointer",
          },
        }, l)),
    ),

    tab === "revenue" && React.createElement("div", {
      className: "wrap-row",
      style: { display: "flex", gap: "8px", alignItems: "center",
               marginBottom: "16px" },
    },
      React.createElement("span", {
        style: { fontSize: "12px", color: C.textMuted },
      }, "Covering the last"),
      ...RANGES.map((r) => React.createElement("button", {
        key: r.days, onClick: () => setDays(r.days),
        style: {
          border: `1px solid ${days === r.days ? C.navy : C.warmGray}`,
          background: days === r.days ? C.navy : "#fff",
          color: days === r.days ? "#fff" : C.navy,
          borderRadius: "6px", padding: "5px 11px", fontSize: "12px",
          fontWeight: 600, cursor: "pointer",
        },
      }, r.label)),
    ),

    error && React.createElement("div", {
      style: { fontSize: "13px", color: C.textDark, background: C.warmWhite,
               border: `1px solid ${C.warmGray}`, borderRadius: "8px",
               padding: "11px 14px", marginBottom: "16px" },
    }, error),

    !data && !error
      ? React.createElement("div", {
          style: { display: "flex", gap: "8px", alignItems: "center",
                   padding: "30px 0" },
        },
          React.createElement(Spinner, { size: 15 }),
          React.createElement("span", {
            style: { fontSize: "13px", color: C.textMuted },
          }, "Adding it up…"))
      : data && React.createElement("div", null,

          // Headline. The one number Aaron opens this page for.
          React.createElement("div", {
            style: { padding: "4px 0 18px",
                     borderBottom: `1px solid ${C.warmGray}`,
                     marginBottom: "18px" },
          },
            React.createElement("div", {
              style: { fontSize: "40px", fontWeight: 700, color: C.navy,
                       fontFamily: "'Playfair Display',serif",
                       fontVariantNumeric: "tabular-nums", lineHeight: 1.1 },
            }, money(tab === "costs" ? data.totals.outstanding
                                     : data.totals.gross)),
            React.createElement("div", {
              style: { fontSize: "12px", color: C.textMuted,
                       textTransform: "uppercase", letterSpacing: "0.06em" },
            }, tab === "costs"
                 ? `Out on ${data.totals.matters_with_outstanding} matters`
                 : `Gross across ${data.totals.count} settlements`),
          ),

          // Supporting figures.
          React.createElement("div", {
            className: "wrap-row",
            style: { display: "flex", gap: "28px", flexWrap: "wrap",
                     marginBottom: "20px" },
          },
            ...Object.entries(data.totals)
              .filter(([k]) => k !== (tab === "costs" ? "outstanding" : "gross"))
              .map(([k, v]) => React.createElement("div", { key: k },
                React.createElement("div", {
                  style: { fontSize: "17px", fontWeight: 700, color: C.navy,
                           fontVariantNumeric: "tabular-nums" },
                }, /advanced|recovered|written|gross|average/.test(k)
                     ? money(v) : String(v)),
                React.createElement("div", {
                  style: { fontSize: "11px", color: C.textMuted,
                           textTransform: "uppercase", letterSpacing: "0.06em" },
                }, k.replace(/_/g, " ")),
              )),
          ),

          // Fees, absent with a reason rather than blank.
          tab === "revenue" && !data.fees_available
            && React.createElement("div", {
                 style: { background: C.warmWhite, border: `1px solid ${C.warmGray}`,
                          borderRadius: "9px", padding: "13px 16px",
                          marginBottom: "20px", fontSize: "13px",
                          color: C.textDark, lineHeight: 1.55 },
               },
                 React.createElement("strong", null,
                   "This is gross, not what the firm made. "),
                 "Fees cannot be computed until the firm's fee terms are "
                 + "entered — flat or stepped, and taken before or after "
                 + "costs come off. Firm Settings → Fee agreement."),

          React.createElement(ByAttorney, {
            rows: data.by_attorney || [], tab, onMatterClick,
          }),

          (data.caveats || []).length > 0 && React.createElement("div", {
            style: { background: C.warmWhite, border: `1px solid ${C.warmGray}`,
                     borderRadius: "9px", padding: "12px 15px",
                     marginTop: "22px", fontSize: "12.5px",
                     color: C.textMuted, lineHeight: 1.6 },
          },
            React.createElement("div", {
              style: { fontWeight: 700, color: C.navy, marginBottom: "3px" },
            }, "What this does not include"),
            React.createElement("ul", { style: { margin: "0 0 0 17px", padding: 0 } },
              ...data.caveats.map((c, i) =>
                React.createElement("li", { key: i }, c)),
            ),
          ),
        ),
  );
}


// Click an attorney to see their matters — the shape Aaron asked for,
// matching the attorney workload card he already uses.
function ByAttorney({ rows, tab, onMatterClick }) {
  const [open, setOpen] = useState(null);
  if (!rows.length) {
    return React.createElement("div", {
      style: { background: "#fff", border: `1px solid ${C.warmGray}`,
               borderRadius: "10px", padding: "20px", fontSize: "13px",
               color: C.textMuted, lineHeight: 1.55 },
    }, "Nothing to break down by attorney yet. The notes below say "
     + "whether that is because there is nothing, or because something "
     + "could not be read.");
  }

  const max = Math.max(...rows.map((r) =>
    tab === "costs" ? r.outstanding : r.gross), 1);

  return React.createElement("div", null,
    React.createElement("h2", {
      style: { fontSize: "13px", color: C.navy, fontWeight: 700,
               textTransform: "uppercase", letterSpacing: "0.06em",
               marginBottom: "10px" },
    }, "By attorney"),

    React.createElement("div", {
      style: { border: `1px solid ${C.warmGray}`, borderRadius: "10px",
               background: "#fff", overflow: "hidden" },
    },
      ...rows.map((r, i) => {
        const value = tab === "costs" ? r.outstanding : r.gross;
        const isOpen = open === r.name;
        const canOpen = tab === "costs" && (r.matters || []).length > 0;
        return React.createElement("div", { key: r.name },
          React.createElement("div", {
            className: "wrap-row",
            onClick: () => canOpen && setOpen(isOpen ? null : r.name),
            style: {
              display: "flex", alignItems: "center", gap: "12px",
              padding: "12px 16px",
              borderTop: i === 0 ? "none" : `1px solid ${C.warmGray}55`,
              cursor: canOpen ? "pointer" : "default",
            },
          },
            React.createElement("span", {
              style: { fontSize: "13.5px", color: C.textDark, fontWeight: 600,
                       minWidth: "150px" },
            }, r.name),
            // Proportion, not severity. A bar showing share of the
            // total says something true; a red one would assert a
            // threshold nobody set.
            React.createElement("div", {
              style: { flex: 1, minWidth: "60px", height: "6px",
                       borderRadius: "3px", background: C.warmGray,
                       overflow: "hidden" },
            },
              React.createElement("div", {
                style: { width: `${(value / max) * 100}%`, height: "100%",
                         background: C.navy },
              })),
            React.createElement("span", {
              style: { fontSize: "13.5px", color: C.navy, fontWeight: 700,
                       fontVariantNumeric: "tabular-nums", minWidth: "90px",
                       textAlign: "right" },
            }, money(value)),
            React.createElement("span", {
              style: { fontSize: "11.5px", color: C.textLight,
                       minWidth: "76px", textAlign: "right" },
            }, tab === "costs"
                 ? `${r.matter_count} matter${r.matter_count === 1 ? "" : "s"}`
                 : `${r.count} settled`),
          ),

          isOpen && React.createElement("div", {
            style: { padding: "0 16px 12px 16px", background: C.warmWhite },
          },
            ...r.matters.map((m) => React.createElement("button", {
              key: m,
              onClick: () => onMatterClick && onMatterClick({ id: Number(m) }),
              style: { display: "block", border: "none",
                       background: "transparent", padding: "4px 0",
                       fontSize: "12.5px", color: C.navy, cursor: "pointer",
                       textDecoration: "underline" },
            }, m)),
          ),
        );
      }),
    ),
  );
}
